/**
 * DevisLineMaterials — panneau interne (non imprimé sur le PDF) affiché
 * sous chaque ligne de devis. Permet de :
 *  - lister/ajouter/supprimer les matériaux consommés pour cette ligne
 *  - saisir un éventuel sous-traitant (nom, prix HT, description)
 *  - auto-suggérer depuis le catalogue Fournisseurs (search similar)
 *  - enregistrer automatiquement chaque matériau saisi dans le catalogue
 *    au moment de la sauvegarde du devis (parent).
 *
 * Contrat props :
 *   value       : { materials: [], sous_traitant: null|{} }
 *   onChange    : (nextValue) => void
 *   readOnly    : bool
 *   lineIndex   : number   (utilisé pour les data-testid)
 */
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, ExternalLink, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

const emptyMaterial = () => ({
  nom: '',
  reference: '',
  fournisseur: '',
  prix_unitaire_ht: 0,
  quantite: 1,
  unite: 'u',
  lien_url: '',
});

const emptySousTraitant = () => ({ nom: '', prix_ht: 0, description: '' });

export default function DevisLineMaterials({ value, onChange, readOnly, lineIndex }) {
  const [open, setOpen] = useState(false);
  const materials = value?.materials || [];
  const sousTraitant = value?.sous_traitant || null;

  const nbMat = materials.length;
  const totalMat = materials.reduce(
    (s, m) => s + (Number(m.prix_unitaire_ht) || 0) * (Number(m.quantite) || 0),
    0,
  );
  const totalST = Number(sousTraitant?.prix_ht) || 0;
  const totalInterne = totalMat + totalST;

  const setMaterials = (next) =>
    onChange({ ...(value || {}), materials: next });

  const setSousTraitant = (next) =>
    onChange({ ...(value || {}), sous_traitant: next });

  const addMaterial = () =>
    setMaterials([...materials, emptyMaterial()]);

  const updateMaterial = (idx, field, v) =>
    setMaterials(
      materials.map((m, i) => (i === idx ? { ...m, [field]: v } : m)),
    );

  const removeMaterial = (idx) =>
    setMaterials(materials.filter((_, i) => i !== idx));

  const missing = nbMat === 0;

  return (
    <div
      className={`mt-1 rounded-md border ${
        missing ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-slate-50/50'
      }`}
      data-testid={`line-mat-panel-${lineIndex}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md"
        data-testid={`line-mat-toggle-${lineIndex}`}
      >
        <span className="flex items-center gap-2">
          🔧 Matériaux & sous-traitant
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              missing
                ? 'bg-amber-200 text-amber-900'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {nbMat} matériau{nbMat > 1 ? 'x' : ''}
            {sousTraitant?.nom ? ' + ST' : ''}
          </span>
          <span className="text-[11px] font-normal text-slate-500">
            (interne — jamais imprimé)
          </span>
        </span>
        <span className="flex items-center gap-3">
          <span className="text-slate-600 font-medium">
            Coût interne : <span className="text-slate-900">{totalInterne.toFixed(2)} €</span>
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-3 border-t border-slate-200">
          {missing && !readOnly && (
            <div
              className="text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-1"
              data-testid={`line-mat-warn-${lineIndex}`}
            >
              ⚠️ Aucun matériau — la sauvegarde sera bloquée tant que cette ligne n'a pas au moins un matériau saisi.
            </div>
          )}

          {/* Liste des matériaux */}
          <div className="space-y-2">
            {materials.map((m, idx) => (
              <MaterialRow
                key={idx}
                idx={idx}
                lineIndex={lineIndex}
                material={m}
                readOnly={readOnly}
                onChange={(f, v) => updateMaterial(idx, f, v)}
                onRemove={() => removeMaterial(idx)}
              />
            ))}
          </div>

          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={addMaterial}
              className="text-xs"
              data-testid={`line-mat-add-${lineIndex}`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter un matériau
            </Button>
          )}

          {/* Sous-traitant */}
          <div className="pt-3 border-t border-dashed border-slate-300">
            <div className="text-xs font-semibold text-slate-700 mb-2">
              Sous-traitant (optionnel)
            </div>
            <div className="grid sm:grid-cols-[1fr_120px_1.5fr_40px] gap-2 items-center">
              <Input
                value={sousTraitant?.nom || ''}
                onChange={(e) =>
                  setSousTraitant({
                    ...(sousTraitant || emptySousTraitant()),
                    nom: e.target.value,
                  })
                }
                placeholder="Nom / société sous-traitante"
                className="h-8 text-xs"
                disabled={readOnly}
                data-testid={`line-st-nom-${lineIndex}`}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={sousTraitant?.prix_ht ?? ''}
                onChange={(e) =>
                  setSousTraitant({
                    ...(sousTraitant || emptySousTraitant()),
                    prix_ht: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Prix HT"
                className="h-8 text-xs text-right"
                disabled={readOnly}
                data-testid={`line-st-prix-${lineIndex}`}
              />
              <Input
                value={sousTraitant?.description || ''}
                onChange={(e) =>
                  setSousTraitant({
                    ...(sousTraitant || emptySousTraitant()),
                    description: e.target.value,
                  })
                }
                placeholder="Description prestation"
                className="h-8 text-xs"
                disabled={readOnly}
                data-testid={`line-st-desc-${lineIndex}`}
              />
              {sousTraitant?.nom && !readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSousTraitant(null)}
                  className="text-red-500 hover:text-red-700 h-8 px-2"
                  data-testid={`line-st-clear-${lineIndex}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          {/* Totaux ligne */}
          <div className="pt-3 border-t border-slate-200 text-xs text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
            <span>
              Matériaux : <b className="text-slate-900">{totalMat.toFixed(2)} €</b>
            </span>
            <span>
              Sous-traitant : <b className="text-slate-900">{totalST.toFixed(2)} €</b>
            </span>
            <span>
              Coût interne ligne : <b className="text-blue-700">{totalInterne.toFixed(2)} €</b>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Ligne matériau — supporte la recherche similaire dans le catalogue
 * fournisseurs. Quand l'utilisateur tape un nom > 2 caractères,
 * appelle /api/fournisseurs/catalogue/similar et affiche les 5 meilleures
 * correspondances en suggestion.
 */
function MaterialRow({ material, idx, lineIndex, onChange, onRemove, readOnly }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (readOnly) return;
    if (!material.nom || material.nom.length < 3) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/fournisseurs/catalogue/similar', {
          params: { q: material.nom, limit: 5 },
        });
        setSuggestions(r.data?.results || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [material.nom, readOnly]);

  const applySuggestion = (s) => {
    onChange('nom', s.nom);
    onChange('reference', s.reference || '');
    onChange('fournisseur', s.fournisseur || '');
    onChange('prix_unitaire_ht', s.prix_unitaire || 0);
    onChange('unite', s.unite || 'u');
    onChange('lien_url', s.lien_url || '');
    setShowSuggestions(false);
  };

  return (
    <div className="rounded border border-slate-200 bg-white p-2 space-y-2">
      <div className="grid sm:grid-cols-[1fr_120px_100px_1fr_40px] gap-2 items-start">
        <div className="relative">
          <Input
            value={material.nom}
            onChange={(e) => {
              onChange('nom', e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Désignation matériau"
            className="h-8 text-xs pr-6"
            disabled={readOnly}
            data-testid={`mat-nom-${lineIndex}-${idx}`}
          />
          <Search className="w-3 h-3 absolute right-2 top-2.5 text-slate-400" />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((s) => (
                <button
                  key={s.material_id}
                  type="button"
                  onClick={() => applySuggestion(s)}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0"
                  data-testid={`mat-suggest-${lineIndex}-${idx}-${s.material_id}`}
                >
                  <div className="font-medium text-slate-900">{s.nom}</div>
                  <div className="text-[10px] text-slate-500 flex justify-between">
                    <span>
                      {s.fournisseur || '—'} · {s.reference || '(sans réf.)'}
                    </span>
                    <span className="font-semibold text-blue-700">
                      {(s.prix_unitaire || 0).toFixed(2)} €/{s.unite || 'u'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={material.prix_unitaire_ht ?? ''}
          onChange={(e) => onChange('prix_unitaire_ht', parseFloat(e.target.value) || 0)}
          placeholder="P.U. HT"
          className="h-8 text-xs text-right"
          disabled={readOnly}
          data-testid={`mat-prix-${lineIndex}-${idx}`}
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          value={material.quantite ?? ''}
          onChange={(e) => onChange('quantite', parseFloat(e.target.value) || 0)}
          placeholder="Qté"
          className="h-8 text-xs text-right"
          disabled={readOnly}
          data-testid={`mat-qte-${lineIndex}-${idx}`}
        />
        <Input
          value={material.fournisseur}
          onChange={(e) => onChange('fournisseur', e.target.value)}
          placeholder="Fournisseur"
          className="h-8 text-xs"
          disabled={readOnly}
          data-testid={`mat-fourn-${lineIndex}-${idx}`}
        />
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 h-8 px-2"
            data-testid={`mat-remove-${lineIndex}-${idx}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      <div className="grid sm:grid-cols-[120px_1fr] gap-2">
        <Input
          value={material.reference}
          onChange={(e) => onChange('reference', e.target.value)}
          placeholder="Référence"
          className="h-7 text-xs"
          disabled={readOnly}
          data-testid={`mat-ref-${lineIndex}-${idx}`}
        />
        <div className="flex gap-1">
          <Input
            value={material.lien_url}
            onChange={(e) => onChange('lien_url', e.target.value)}
            placeholder="Lien externe (https://...)"
            className="h-7 text-xs flex-1"
            disabled={readOnly}
            data-testid={`mat-lien-${lineIndex}-${idx}`}
          />
          {material.lien_url && (
            <a
              href={material.lien_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 h-7 flex items-center px-2"
              data-testid={`mat-open-${lineIndex}-${idx}`}
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
